import React, { useState } from 'react';
import { FilePlus2, XCircle } from 'lucide-react';
import {
  Button,
  ConfirmationDialog,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import {
  abandonReplacementClaim,
  createReplacementClaim,
} from '../../services/api';
import type { EpisodicClaimStatus } from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useToast } from '../../hooks/useToastContext';

/**
 * Open or close a denied claim's correction draft.
 *
 * "Create replacement" starts the next claim version in the cycle's chain: a
 * REPLACEMENT draft with a fresh unique PCN, chained to the denied predecessor,
 * its reviewed worksheet copied and prepopulated with the correction values.
 * Nothing is payer-facing yet, so it needs no confirmation.
 *
 * "Abandon" closes the cycle's unsent replacement draft and returns the cycle
 * to its denied state. The draft's correction work is discarded, so it confirms
 * first. Abandoning an unsent draft is not a payer void: nothing is transmitted.
 *
 * Both controls hide entirely from users without `claims:submit` — the backend
 * route refuses them the same action, so showing the button would only offer a
 * click that fails. Hiding is UX; the route gate is the boundary of truth.
 */
export const ReplacementAction: React.FC<{
  billingCycleId: string;
  billingCycleStatus: EpisodicClaimStatus;
  claimCorrectionKind?: 'replacement' | 'resubmission' | null;
  /** Reload the row and the tab counts after a successful mutation. */
  onDone: () => void | Promise<void>;
}> = ({ billingCycleId, billingCycleStatus, claimCorrectionKind, onDone }) => {
  // Selector shape 3 (frontend-navigation-gating): the store returns the
  // boolean, so a grant or revoke re-renders this row without another cause.
  const canSubmitClaims = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_SUBMIT)
  );
  const [busy, setBusy] = useState(false);
  const [confirmingAbandon, setConfirmingAbandon] = useState(false);
  const { error: showError, success: showSuccess } = useToast();

  if (!canSubmitClaims) return null;

  const run = async (
    action: () => Promise<{ error?: unknown }>,
    fallbackMessage: string,
    successMessage: string
  ) => {
    setBusy(true);
    try {
      // The whole result, not just `.error`: the status lives on
      // `result.response`, and without it getBusinessErrorMessage cannot tell a
      // 5xx body from a 4xx business message and would show the server's text.
      const result = await action();
      if (result.error) throw result;
    } catch (error) {
      // Never the raw server string: patient data and denial descriptions both
      // travel in these responses.
      console.error('Failed to act on the replacement claim:', error);
      showError(getBusinessErrorMessage(error) ?? fallbackMessage);
      setBusy(false);
      return;
    }

    showSuccess(successMessage);
    try {
      await onDone();
    } catch (error) {
      // The mutation already succeeded, so a refresh failure is its own
      // message — saying the action failed would invite the biller to repeat it.
      console.error('Failed to refresh after a replacement action:', error);
      showError('Done. Refresh the page to see the updated list.');
    } finally {
      setBusy(false);
    }
  };

  if (billingCycleStatus === 'DENIED') {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              disabled={busy}
              aria-label='Create replacement'
              className='text-muted-foreground hover:text-foreground'
              onClick={() =>
                void run(
                  () =>
                    createReplacementClaim({
                      path: { billing_cycle_id: billingCycleId },
                    }),
                  'Unable to create the replacement claim.',
                  'Replacement draft created'
                )
              }
            >
              <FilePlus2 className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='text-xs'>
            Create replacement
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (
    billingCycleStatus === 'CORRECTION_DRAFT' ||
    billingCycleStatus === 'PENDING_APPROVAL' ||
    billingCycleStatus === 'READY_TO_SEND'
  ) {
    const correctionName =
      claimCorrectionKind === 'resubmission' ? 'resubmission' : 'replacement';
    return (
      <>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                disabled={busy}
                aria-label={`Abandon ${correctionName} draft`}
                className='text-muted-foreground hover:text-foreground'
                onClick={() => setConfirmingAbandon(true)}
              >
                <XCircle className='h-4 w-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='bottom' className='text-xs'>
              Abandon {correctionName} draft
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <ConfirmationDialog
          open={confirmingAbandon}
          title={`Abandon ${correctionName} draft`}
          message={`This closes the unsent ${correctionName} draft and returns the cycle to its prior status. The correction work on the draft worksheet is discarded. Nothing is sent to the payer.`}
          confirmLabel='Abandon'
          loading={busy}
          onAccept={() =>
            void run(
              () =>
                abandonReplacementClaim({
                  path: { billing_cycle_id: billingCycleId },
                }),
              `Unable to abandon the ${correctionName} draft.`,
              `${correctionName === 'resubmission' ? 'Resubmission' : 'Replacement'} draft abandoned`
            ).finally(() => setConfirmingAbandon(false))
          }
          onCancel={() => setConfirmingAbandon(false)}
        />
      </>
    );
  }

  return null;
};

export default ReplacementAction;
